import React from 'react';

function formatCurrency(value) {
  return `$${Math.round(value).toLocaleString()}`;
}

export function TrendLineChart({ points }) {
  if (!points || points.length === 0) {
    return <p className="chart-empty">No trend data available.</p>;
  }

  const width = 520;
  const height = 220;
  const padding = 24;
  const values = points.map((item) => Number(item.value || 0));
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 1);
  const range = Math.max(max - min, 1);

  const coords = points.map((item, index) => {
    const x = padding + (index * (width - padding * 2)) / Math.max(points.length - 1, 1);
    const y = height - padding - ((Number(item.value || 0) - min) / range) * (height - padding * 2);
    return { x, y, label: item.label, value: Number(item.value || 0) };
  });

  const linePath = coords.map((c, index) => `${index === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${height - padding} L ${coords[0].x} ${height - padding} Z`;

  return (
    <div className="chart-shell">
      <svg className="trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Monthly savings trend">
        <defs>
          <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(76,193,228,0.42)" />
            <stop offset="100%" stopColor="rgba(31,165,143,0.06)" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#trendArea)" />
        <path d={linePath} fill="none" stroke="#4cc1e4" strokeWidth="3" strokeLinecap="round" />
        {coords.map((c) => (
          <g key={c.label}>
            <circle cx={c.x} cy={c.y} r="4" fill="#c1f6ff" />
            <text x={c.x} y={height - 6} textAnchor="middle" className="chart-axis-label">{c.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export function DonutChart({ items, title }) {
  if (!items || items.length === 0) {
    return <p className="chart-empty">No {title.toLowerCase()} data available.</p>;
  }

  const normalizedItems = items.map((item) => ({
    label: item.bucket || item.category || item.status || item.provider || 'unknown',
    count: Number(item.count || 0),
  }));

  const total = normalizedItems.reduce((acc, item) => acc + item.count, 0);
  if (total <= 0) {
    return <p className="chart-empty">No {title.toLowerCase()} data available.</p>;
  }

  const palette = ['#4cc1e4', '#1fa58f', '#f1c75b', '#ff8a65', '#90caf9', '#80cbc4'];
  let offset = 0;
  const gradient = normalizedItems.map((item, index) => {
    const percentage = (item.count / total) * 100;
    const start = offset;
    offset += percentage;
    return `${palette[index % palette.length]} ${start}% ${offset}%`;
  }).join(', ');

  return (
    <div className="donut-wrap">
      <div className="donut" style={{ background: `conic-gradient(${gradient})` }} aria-hidden="true">
        <div className="donut-center">{total}</div>
      </div>
      <div className="donut-legend">
        {normalizedItems.map((item, index) => (
          <div className="legend-item" key={`${title}-${item.label}-${index}`}>
            <span className="legend-dot" style={{ backgroundColor: palette[index % palette.length] }} />
            <span>{item.label}: {item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DeltaBarChart({ points }) {
  if (!points || points.length === 0) {
    return <p className="chart-empty">No trend data available.</p>;
  }

  const width = 560;
  const height = 240;
  const padding = 26;
  const values = points.map((item) => Number(item.value || 0));
  const maxMagnitude = Math.max(...values.map((value) => Math.abs(value)), 1);
  const baselineY = height / 2;
  const barWidth = Math.max(18, (width - padding * 2) / Math.max(points.length * 1.35, 1));
  const gap = (width - padding * 2 - barWidth * points.length) / Math.max(points.length - 1, 1);

  return (
    <div className="chart-shell">
      <svg className="trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Monthly value delta chart">
        <line x1={padding} y1={baselineY} x2={width - padding} y2={baselineY} className="chart-axis-line" />
        {points.map((item, index) => {
          const value = Number(item.value || 0);
          const x = padding + index * (barWidth + gap);
          const barHeight = (Math.abs(value) / maxMagnitude) * (height / 2 - padding);
          const y = value >= 0 ? baselineY - barHeight : baselineY;
          const isPositive = value >= 0;
          return (
            <g key={`${item.label}-${index}`}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="4"
                fill={isPositive ? '#2ec6b1' : '#ff8a65'}
                opacity="0.9"
              />
              <text x={x + barWidth / 2} y={height - 8} textAnchor="middle" className="chart-axis-label">
                {item.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function ScoreGauge({ score, label, subtitle }) {
  const clamped = Math.max(0, Math.min(100, Number(score || 0)));
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = (clamped / 100) * circumference;

  return (
    <div className="gauge-wrap" aria-label={label}>
      <svg viewBox="0 0 160 160" className="gauge-svg" role="img" aria-label={`${label} ${Math.round(clamped)} percent`}>
        <circle cx="80" cy="80" r={radius} className="gauge-track" />
        <circle
          cx="80"
          cy="80"
          r={radius}
          className="gauge-progress"
          strokeDasharray={`${progress} ${circumference - progress}`}
        />
        <text x="80" y="78" textAnchor="middle" className="gauge-value">{Math.round(clamped)}%</text>
        <text x="80" y="98" textAnchor="middle" className="gauge-label">{subtitle}</text>
      </svg>
      <p className="gauge-title">{label}</p>
    </div>
  );
}

export function RankedBarChart({ items, labelKey, valueKey, title, formatter }) {
  if (!items || items.length === 0) {
    return <p className="chart-empty">No {title.toLowerCase()} data available.</p>;
  }

  const normalized = items
    .map((item) => ({
      label: item[labelKey] || item.bucket || item.category || item.status || 'unknown',
      value: Number(item[valueKey] || item.count || 0),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  if (normalized.length === 0) {
    return <p className="chart-empty">No {title.toLowerCase()} data available.</p>;
  }

  const maxValue = Math.max(...normalized.map((item) => item.value), 1);

  return (
    <div className="ranked-bars" role="img" aria-label={title}>
      {normalized.map((item) => {
        const pct = (item.value / maxValue) * 100;
        const renderedValue = formatter ? formatter(item.value) : item.value.toLocaleString();
        return (
          <div className="ranked-row" key={`${title}-${item.label}`}>
            <div className="ranked-row-header">
              <span>{item.label}</span>
              <strong>{renderedValue}</strong>
            </div>
            <div className="ranked-track">
              <div className="ranked-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { formatCurrency };
